// pages/Services/components/CallToAction.js
import React from 'react';
import { Typography, Button, Row, Col } from 'antd';
import useResponsive from '../../../hooks/useResponsive';
import {
  PhoneOutlined,
  WhatsAppOutlined,
  CalendarOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const CallToAction = ({ theme }) => {
  const { isMobile } = useResponsive();
  const getThemeStyles = () => {
    if (theme === 'dark') {
      return {
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
        textPrimary: '#ffffff',
        textSecondary: 'rgba(255, 255, 255, 0.75)',
        gradientPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        cardBg: 'rgba(255, 255, 255, 0.05)',
        cardBorder: 'rgba(255, 255, 255, 0.1)',
      };
    } else {
      return {
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.05) 100%)',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        gradientPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        cardBg: 'rgba(255, 255, 255, 0.8)',
        cardBorder: 'rgba(102, 126, 234, 0.15)',
      };
    }
  };

  const themeStyles = getThemeStyles();

  const contactMethods = [
    {
      icon: <WhatsAppOutlined style={{ fontSize: '24px' }} />,
      title: "WhatsApp",
      description: "Quick response within minutes",
      action: "Chat Now",
      color: "#25D366",
      onClick: () => {
        const phoneNumber = "+919876543210";
        const message = "Hi, I'm interested in your wellness programs. Please provide more information.";
        window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
      }
    },
    {
      icon: <PhoneOutlined style={{ fontSize: '24px' }} />,
      title: "Phone Call",
      description: "Direct consultation available",
      action: "Call Now",
      color: "#667eea",
      onClick: () => {
        window.location.href = "tel:+919876543210";
      }
    },
    {
      icon: <CalendarOutlined style={{ fontSize: '24px' }} />,
      title: "Schedule Meeting",
      description: "Book a convenient time slot",
      action: "Schedule",
      color: "#764ba2",
      onClick: () => {
        // Integrate with your booking system
        window.open("https://calendly.com/your-link", '_blank');
      }
    }
  ];



  return (
    <div style={{
      padding: '40px 20px 20px',
      background: themeStyles.background,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background Decorations */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '-5%',
        width: '300px',
        height: '300px',
        background: themeStyles.gradientPrimary,
        borderRadius: '50%',
        opacity: 0.1,
        filter: 'blur(60px)',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '-5%',
        width: '250px',
        height: '250px',
        background: themeStyles.gradientPrimary,
        borderRadius: '50%',
        opacity: 0.1,
        filter: 'blur(50px)',
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Main CTA Section */}
        <div style={{
          background: themeStyles.cardBg,
          borderRadius: '24px',
          padding: isMobile ? '24px 16px' : '32px 24px',
          textAlign: 'center',
          border: `1px solid ${themeStyles.cardBorder}`,
          backdropFilter: 'blur(20px)',
          marginBottom: '24px',
        }}>
          <div style={{ marginBottom: '24px' }}>
            <Title
              level={2}
              style={{
                color: themeStyles.textPrimary,
                marginBottom: '16px',
                fontSize: '2.5rem',
                fontWeight: '700',
                background: themeStyles.gradientPrimary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Ready to Transform Your Health?
            </Title>

            <Paragraph style={{
              color: themeStyles.textSecondary,
              fontSize: '1.2rem',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: '1.6',
            }}>
              Join hundreds of satisfied clients who have transformed their lives through
              Dr. Gundala Abhinaya's personalized wellness programs.
            </Paragraph>


          </div>

          {/* Contact Methods */}
          <div style={{
            margin: isMobile ? '16px 0' : '0 0 16px 0'
          }}>
            {isMobile ? (
              // Mobile View: Side-by-side Compact Icons
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns side-by-side
                gap: '8px',
                padding: '0'
              }}>
                {contactMethods.map((method, index) => (
                  <div
                    key={index}
                    onClick={method.onClick}
                    style={{
                      background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
                      border: `1px solid ${themeStyles.cardBorder}`,
                      borderRadius: '24px', // More rounded
                      padding: '16px 4px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      height: '160px', // Reduced height
                      boxShadow: theme === 'dark' ? 'none' : `0 4px 12px rgba(0,0,0,0.05)`,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{
                      color: method.color,
                      fontSize: '40px', // Even bigger icon
                      background: `${method.color}15`,
                      padding: '8px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '72px', // Even bigger container
                      height: '72px'
                    }}>
                      {method.icon}
                    </div>
                    <Text strong style={{
                      color: themeStyles.textPrimary,
                      fontSize: '15px', // Even bigger text
                      textAlign: 'center',
                      lineHeight: '1.3',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      padding: '0 2px',
                      width: '100%',
                      wordBreak: 'break-word'
                    }}>
                      {method.title}
                    </Text>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop View: Original Large Cards
              <Row gutter={[24, 24]} justify="center">
                {contactMethods.map((method, index) => (
                  <Col key={index} xs={24} sm={8} md={6}>
                    <div
                      style={{
                        background: theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(255, 255, 255, 0.9)',
                        border: `1px solid ${themeStyles.cardBorder}`,
                        borderRadius: '24px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100%',
                        boxShadow: theme === 'dark' ? 'none' : `0 10px 30px rgba(0,0,0,0.05)`
                      }}
                      onClick={method.onClick}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.borderColor = method.color;
                        e.currentTarget.style.boxShadow = `0 12px 40px ${method.color}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = themeStyles.cardBorder;
                        e.currentTarget.style.boxShadow = theme === 'dark' ? 'none' : `0 10px 30px rgba(0,0,0,0.05)`;
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: `${method.color}15`,
                        color: method.color,
                        marginBottom: '20px',
                        fontSize: '28px',
                        border: `1px solid ${method.color}30`
                      }}>
                        {method.icon}
                      </div>

                      <Title level={4} style={{
                        color: themeStyles.textPrimary,
                        marginBottom: '8px',
                        fontSize: '18px',
                        fontWeight: '700'
                      }}>
                        {method.title}
                      </Title>

                      <Text style={{
                        color: themeStyles.textSecondary,
                        fontSize: '14px',
                        display: 'block',
                        marginBottom: '24px',
                        flex: 1
                      }}>
                        {method.description}
                      </Text>

                      <Button
                        type="primary"
                        shape="round"
                        size="large"
                        style={{
                          background: themeStyles.gradientPrimary,
                          border: 'none',
                          height: '48px',
                          padding: '0 32px',
                          fontWeight: '600',
                          boxShadow: `0 4px 12px ${method.color}40`,
                          width: '100%',
                          maxWidth: '200px'
                        }}
                      >
                        {method.action}
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div style={{ textAlign: 'center' }}>
          {isMobile ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
              padding: '0 4px'
            }}>
              {[
                { title: 'Free Consultation', desc: 'Get personalized health assessment and program recommendations' },
                { title: 'Flexible Scheduling', desc: 'Online and offline sessions available to fit your lifestyle' },
                { title: 'Ongoing Support', desc: '24/7 guidance and support throughout your wellness journey' }
              ].map((item, index) => (
                <div key={index} style={{
                  background: themeStyles.cardBg,
                  border: `1px solid ${themeStyles.cardBorder}`,
                  borderRadius: '16px',
                  padding: '12px 4px',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: '140px',
                  gap: '6px'
                }}>
                  <Title level={5} style={{
                    color: themeStyles.textPrimary,
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: '1.2',
                    fontWeight: '700'
                  }}>
                    {item.title}
                  </Title>
                  <Text style={{
                    color: themeStyles.textSecondary,
                    fontSize: '12px',
                    lineHeight: '1.3',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    opacity: 0.9
                  }}>
                    {item.desc}
                  </Text>
                </div>
              ))}
            </div>
          ) : (
            <Row gutter={[24, 16]} justify="center">
              <Col xs={24} sm={12} md={8}>
                <div style={{
                  background: themeStyles.cardBg,
                  border: `1px solid ${themeStyles.cardBorder}`,
                  borderRadius: '12px',
                  padding: '16px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <Title level={5} style={{ color: themeStyles.textPrimary, marginBottom: '8px' }}>
                    Free Consultation
                  </Title>
                  <Text style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
                    Get personalized health assessment and program recommendations
                  </Text>
                </div>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <div style={{
                  background: themeStyles.cardBg,
                  border: `1px solid ${themeStyles.cardBorder}`,
                  borderRadius: '12px',
                  padding: '16px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <Title level={5} style={{ color: themeStyles.textPrimary, marginBottom: '8px' }}>
                    Flexible Scheduling
                  </Title>
                  <Text style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
                    Online and offline sessions available to fit your lifestyle
                  </Text>
                </div>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <div style={{
                  background: themeStyles.cardBg,
                  border: `1px solid ${themeStyles.cardBorder}`,
                  borderRadius: '12px',
                  padding: '16px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <Title level={5} style={{ color: themeStyles.textPrimary, marginBottom: '8px' }}>
                    Ongoing Support
                  </Title>
                  <Text style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
                    24/7 guidance and support throughout your wellness journey
                  </Text>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallToAction;